import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Typography,
  Stack,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { uiConfig } from '../config';
import { DifficultyLevel, DIFFICULTY_LABELS, DIFFICULTY_CONFIG } from '../constants/trainingConstants';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { navigateToTrainingSession } from '../utils/trainingNavigation';
import { useConfirm } from '../components/ui/ConfirmDialog';
import TrackTypeBadge from '../components/training/TrackTypeBadge';
import { TrackType, TRACK_TYPE_LABELS, TRACK_TYPE_OPTION_LABELS } from '../constants/trainingConstants';
import ReadinessGate from '../components/playbookBuilder/ReadinessGate';
import { fetchReadiness, type PlaybookReadiness } from '../services/playbookPersonaApi';
import { trainingTrackItemCount } from '../utils/trainingTrackItemCount';

const READINESS_CHECK_FAILED =
  'We could not check whether a Sales Playbook track can be created yet. Try again in a moment.';

interface TrainingTrack {
  _id: string;
  trackName: string;
  description: string;
  trackType: 'kb' | 'playbook';
  difficulty: DifficultyLevel;
  status: 'draft' | 'active' | 'archived';
  estimatedDuration: number;
  tags: string[];
  assignedUsers: string[];
  items: any[];
  /** A playbook track's KB items live on its AI customer's agenda. */
  personaConfig?: { agendaItems?: unknown[] | null } | null;
  // Computed live from TrainingSession at read time (see backend getAllTracks) —
  // not a stored/denormalized counter, so it always reflects real session data.
  sessionCount: number;
  // Training quality score (DB behavioralScore) and completion (abandoned = 0),
  // same definitions as the superadmin portal; null until something is scored.
  avgScore: number | null;
  avgCompletion?: number | null;
  createdAt: string;
  updatedAt: string;
}

interface TrainingTracksProps {
  /**
   * Header slot to render the toolbar into. Omit it for an inline row;
   * pass null while the slot is still mounting so nothing flashes inline.
   */
  actionsContainer?: HTMLElement | null;
  /** Tab-row slot for the filters; same omit / null contract. */
  filtersContainer?: HTMLElement | null;
}

const TrainingTracks: React.FC<TrainingTracksProps> = ({ actionsContainer, filtersContainer }) => {
  const navigate = useNavigate();
  const { isAdmin } = useRoleAccess();
  const confirm = useConfirm();
  const [tracks, setTracks] = useState<TrainingTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(uiConfig.defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [trackTypeFilter, setTrackTypeFilter] = useState<string>('all');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');


  // Fetch tracks
  const fetchTracks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('limit', String(rowsPerPage));

      if (trackTypeFilter !== 'all') {
        params.append('trackType', trackTypeFilter);
      }

      // Difficulty exists only on Sales Playbook tracks, so the filter is
      // offered, and sent, only while the list is narrowed to them.
      if (trackTypeFilter === TrackType.PLAYBOOK && difficultyFilter !== 'all') {
        params.append('difficulty', difficultyFilter);
      }

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await apiService.get(`/training/tracks?${params.toString()}`);

      if (response.success) {
        setTracks(response.data.tracks || []);
        setTotalCount(response.data.pagination?.total || 0);
      }
    } catch (error: any) {
      console.error('Error fetching training tracks:', error);
      setError(error.error || error.message || 'Failed to fetch training tracks');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, trackTypeFilter, difficultyFilter, statusFilter]);

  useEffect(() => {
    fetchTracks();
  }, [fetchTracks]);

  // Create-track type-picker popup, anchored to the Create Track button
  const [createMenuAnchor, setCreateMenuAnchor] = useState<HTMLElement | null>(null);

  const handleCreateTrackClick = (event: React.MouseEvent<HTMLElement>) => {
    setCreateMenuAnchor(event.currentTarget);
  };

  // A Sales Playbook track cannot be built without company details and a
  // playbook, so that is checked here, before the admin reaches the builder.
  // The builder keeps its own gate as a backstop for direct links.
  const [isCheckingReadiness, setIsCheckingReadiness] = useState(false);
  const [blockedReadiness, setBlockedReadiness] = useState<PlaybookReadiness | null>(null);

  const openBuilder = (trackType: TrackType) => navigate(`/training/builder?trackType=${trackType}`);

  const handleCreateTrackTypeSelected = async (trackType: TrackType) => {
    setCreateMenuAnchor(null);
    if (trackType !== TrackType.PLAYBOOK) {
      openBuilder(trackType);
      return;
    }
    setError(null);
    setIsCheckingReadiness(true);
    try {
      const readiness = await fetchReadiness();
      if (readiness?.ready) {
        openBuilder(trackType);
        return;
      }
      setBlockedReadiness(readiness);
    } catch (readinessError) {
      console.error('Error checking playbook track readiness:', readinessError);
      setError(READINESS_CHECK_FAILED);
    } finally {
      setIsCheckingReadiness(false);
    }
  };

  // Handle edit track
  const handleEditTrack = (track: TrainingTrack) => {
    navigate(`/training/builder/${track._id}`);
  };

  // Handle delete track
  const handleDeleteTrack = async (track: TrainingTrack) => {
    if (!(await confirm({
      title: 'Delete Training Track',
      message: `Are you sure you want to delete the track "${track.trackName}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'destructive',
    }))) {
      return;
    }

    try {
      setError(null);
      const response = await apiService.delete(`/training/tracks/${track._id}`);

      if (response.success) {
        setSuccess('Training track deleted successfully');
        fetchTracks();
      }
    } catch (error: any) {
      console.error('Error deleting training track:', error);
      setError(error.error || error.message || 'Failed to delete training track');
    }
  };

  // Handle publish/unpublish track
  const handleTogglePublish = async (track: TrainingTrack) => {
    try {
      setError(null);

      const endpoint = track.status === 'active'
        ? `/training/tracks/${track._id}/unpublish`
        : `/training/tracks/${track._id}/publish`;

      const response = await apiService.post(endpoint);

      if (response.success) {
        setSuccess(track.status === 'active' ? 'Track unpublished' : 'Track published');
        fetchTracks();
      }
    } catch (error: any) {
      console.error('Error toggling track publish status:', error);
      setError(error.error || error.message || 'Failed to update track status');
    }
  };

  // TODO(cleanup): unreferenced today. Kept rather than deleted so removing it is a
  // deliberate, reviewed change instead of a side effect of the lint pass.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAssignUsers = (track: TrainingTrack) => {
    navigate(`/training/tracks/${track._id}/assign`);
  };

  // Handle start training (navigate to frontend via SSO)
  const handleStartTraining = async (track: TrainingTrack) => {
    try {
      setError(null);
      await navigateToTrainingSession(track._id, track.trackName);
      setSuccess('Opening training session in new tab...');
    } catch (error: any) {
      console.error('Error starting training session:', error);
      setError(error.error || error.message || 'Failed to start training session');
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'draft':
        return 'default';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };

  const toolbar = (
    <Box display="flex" alignItems="center" gap={1}>
      <IconButton onClick={fetchTracks} color="primary" aria-label="Refresh tracks">
        <RefreshIcon />
      </IconButton>
      {isAdmin && (
        <Button
          variant="contained"
          startIcon={isCheckingReadiness ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          onClick={handleCreateTrackClick}
          disabled={isCheckingReadiness}
          data-tutorial="create-track-button"
        >
          Create Track
        </Button>
      )}
    </Box>
  );

  // Filters change the query, so they always start from the first page.
  const filters = (
    <Box display="flex" gap={1} alignItems="center" data-tutorial="training-filters">
      <FormControl size="small" sx={{ minWidth: 170 }}>
        <InputLabel id="training-track-type-label">Track type</InputLabel>
        <Select
          labelId="training-track-type-label"
          value={trackTypeFilter}
          onChange={(e) => {
            setTrackTypeFilter(e.target.value);
            if (e.target.value !== TrackType.PLAYBOOK) setDifficultyFilter('all');
            setPage(0);
          }}
          label="Track type"
        >
          <MenuItem value="all">All</MenuItem>
          {Object.values(TrackType).map((type) => (
            <MenuItem key={type} value={type}>
              {TRACK_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {trackTypeFilter === TrackType.PLAYBOOK && (
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="training-difficulty-label">Difficulty</InputLabel>
        <Select
          labelId="training-difficulty-label"
          value={difficultyFilter}
          onChange={(e) => { setDifficultyFilter(e.target.value); setPage(0); }}
          label="Difficulty"
        >
          <MenuItem value="all">All</MenuItem>
          {Object.values(DifficultyLevel).map((level) => (
            <MenuItem key={level} value={level}>
              {DIFFICULTY_LABELS[level]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      )}

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="training-track-status-label">Status</InputLabel>
        <Select
          labelId="training-track-status-label"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          label="Status"
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="archived">Archived</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  return (
      <Box>
        {filtersContainer === undefined ? (
          <Box display="flex" justifyContent="flex-end" mb={2}>{filters}</Box>
        ) : filtersContainer ? (
          createPortal(filters, filtersContainer)
        ) : null}
        {actionsContainer === undefined ? (
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
            {toolbar}
          </Box>
        ) : actionsContainer ? (
          createPortal(toolbar, actionsContainer)
        ) : null}

        <Menu
          anchorEl={createMenuAnchor}
          open={Boolean(createMenuAnchor)}
          onClose={() => setCreateMenuAnchor(null)}
        >
          <MenuItem onClick={() => handleCreateTrackTypeSelected(TrackType.KB)}>
            {TRACK_TYPE_OPTION_LABELS[TrackType.KB]}
          </MenuItem>
          <MenuItem onClick={() => handleCreateTrackTypeSelected(TrackType.PLAYBOOK)}>
            {TRACK_TYPE_OPTION_LABELS[TrackType.PLAYBOOK]}
          </MenuItem>
        </Menu>

        <Dialog
          open={Boolean(blockedReadiness)}
          onClose={() => setBlockedReadiness(null)}
          maxWidth="sm"
          fullWidth
          aria-labelledby="playbook-readiness-title"
        >
          <DialogTitle id="playbook-readiness-title">Before you create a Sales Playbook track</DialogTitle>
          <DialogContent>
            {blockedReadiness && <ReadinessGate readiness={blockedReadiness} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBlockedReadiness(null)}>Not now</Button>
          </DialogActions>
        </Dialog>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Track Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Difficulty</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Assigned Users</TableCell>
                  <TableCell>Sessions</TableCell>
                  <TableCell>Avg Training Quality</TableCell>
                  <TableCell>Avg Completion</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : tracks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No training tracks found. Create your first track to get started!
                    </TableCell>
                  </TableRow>
                ) : (
                  tracks.map((track) => (
                    <TableRow key={track._id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {track.trackName}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {track.description?.substring(0, 50)}
                            {track.description?.length > 50 ? '...' : ''}
                          </Typography>
                          {track.tags?.length > 0 && (
                            <Stack direction="row" spacing={0.5} mt={0.5}>
                              {track.tags.slice(0, 2).map((tag, index) => (
                                <Chip key={index} label={tag} size="small" variant="outlined" />
                              ))}
                              {track.tags.length > 2 && (
                                <Chip label={`+${track.tags.length - 2}`} size="small" variant="outlined" />
                              )}
                            </Stack>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <TrackTypeBadge trackType={track.trackType} />
                      </TableCell>
                      <TableCell>
                        {track.trackType === TrackType.PLAYBOOK && (() => {
                          const cfg = DIFFICULTY_CONFIG[track.difficulty];
                          const name = track.difficulty
                            ? track.difficulty.charAt(0).toUpperCase() + track.difficulty.slice(1)
                            : track.difficulty;
                          return (
                            <Box>
                              <Typography variant="body2">
                                {cfg ? `${name}: ${cfg.acceptanceThreshold}% accuracy to pass` : name}
                              </Typography>
                              {cfg && (
                                <Typography variant="body2" color="text.secondary">
                                  {cfg.maxPushbackRounds} pushback attempts
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={track.status}
                          color={getStatusColor(track.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{trainingTrackItemCount(track)}</TableCell>
                      <TableCell>
                        <Chip
                          label={track.assignedUsers?.length || 0}
                          size="small"
                          icon={<PeopleIcon />}
                          clickable
                        />
                      </TableCell>
                      <TableCell>
                        {track.sessionCount || 0}
                      </TableCell>
                      <TableCell>
                        {track.avgScore != null ? `${track.avgScore.toFixed(1)}%` : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {track?.avgCompletion != null ? `${track.avgCompletion.toFixed(1)}%` : 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        {/* Show management actions only for admins */}
                        {isAdmin && (
                          <>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditTrack(track)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={track.status === 'active' ? 'Unpublish' : 'Publish'}>
                              <IconButton
                                size="small"
                                onClick={() => handleTogglePublish(track)}
                                color={track.status === 'active' ? 'warning' : 'success'}
                              >
                                {track.status === 'active' ? <PauseIcon /> : <PlayIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteTrack(track)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}

                        {/* Show "Start Training" for all users (including admins) if track is active */}
                        {track.status === 'active' && (
                          <Tooltip title="Start Training">
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() => handleStartTraining(track)}
                              sx={{ ml: 1 }}
                            >
                              Start
                            </Button>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Box>
  );
};

export default TrainingTracks;
